import { Base64 } from '../../../util/Base64';
import { createApi } from '@reduxjs/toolkit/query/react';
import Util from '../../../util/Util';
import { CUParametersObjAPIResponse } from './CUParameters.model';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { getParameterValue } from './util/parameterUtil';
import { ComposedParametersValue } from '../selectors';
import exponentialPollingPromise from '../../../util/promise/exponentialPollingPromise';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import { ErrorRestST } from '../../../axios/rest.model';

const backendParametersServiceURL = new Util().getParameterServiceURL();

export const ParametersAPI = createApi({
  reducerPath: 'ParametersAPI',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['cu-parameters', 'device-parameters'],
  endpoints(build) {
    return {
      getCUParametersFromCU: build.query<
        CUParametersObjAPIResponse,
        { deviceId: string; controlUnitId: number | string }
      >({
        query: ({ deviceId, controlUnitId }) => ({
          url: `${backendParametersServiceURL}/${Base64.encode(
            deviceId
          )}/${controlUnitId}`,
        }),
        transformResponse: (data: CUParametersObjAPIResponse, _meta, args) =>
          data ??
          // When controlUnit not found it respond empty
          ({
            idCU: args.controlUnitId,
            parameters: {},
          } as CUParametersObjAPIResponse),
        transformErrorResponse: (response) => response as ErrorRestST,
      }),
      getCUParametersFromDevice: build.query<
        CUParametersObjAPIResponse,
        { deviceId: string }
      >({
        query: ({ deviceId }) => ({
          url: `${backendParametersServiceURL}/${Base64.encode(deviceId)}`,
        }),
        providesTags: (result, _error, args) =>
          result
            ? [
                { type: 'device-parameters' as const, id: args.deviceId },
                'device-parameters',
              ]
            : ['device-parameters'],
      }),

      setCUParameters: build.mutation<
        unknown,
        {
          deviceId: string;
          controlUnitId: number | string;
          parameterLevelName: string;
          parameter: ComposedParametersValue;
          parameterValue: string;
          preferredTemperatureUnit: string;
        }
      >({
        async queryFn(
          props,
          api,
          _extraOptions,
          baseQuery
        ): Promise<QueryReturnValue<unknown, unknown, {}>> {
          const {
            controlUnitId,
            deviceId,
            parameterLevelName,
            parameter,
            parameterValue,
            preferredTemperatureUnit,
          } = props;
          const parameterName = parameter.name;

          const parameterObject = {
            idCU: controlUnitId,
            parameters: {
              [parameterLevelName]: [
                {
                  name: parameterName,
                  value: parameterValue,
                },
              ],
            },
          };
          const mutationResponse = await baseQuery({
            url: `${backendParametersServiceURL}/${Base64.encode(
              deviceId
            )}/${controlUnitId}`,
            method: 'PATCH',
            data: parameterObject,
          });
          if (mutationResponse.error) {
            return mutationResponse;
          }
          const getActionStatusResponsePromise = () =>
            api
              .dispatch(
                ParametersAPI.endpoints.getCUParametersFromCU.initiate(
                  { controlUnitId, deviceId },
                  {
                    forceRefetch: true,
                  }
                )
              )
              .unwrap();

          const isActionStatusEqual = (value: CUParametersObjAPIResponse) => {
            const valueIncoming = value.parameters[parameterLevelName].find(
              (parameter) => parameter.name === parameterName
            )?.value;
            const currentValue = getParameterValue(
              {
                ...parameter,
                value: valueIncoming,
              },
              preferredTemperatureUnit
            );
            const desiredValue = getParameterValue(
              {
                ...parameter,
                value: parameterValue,
              },
              preferredTemperatureUnit
            );
            return currentValue === desiredValue;
          };
          return exponentialPollingPromise(
            getActionStatusResponsePromise,
            isActionStatusEqual
          )
            .then(() => mutationResponse)
            .catch((error) => {
              return { error };
            });
        },
        invalidatesTags: (_result, _error, arg) => [
          { type: 'device-parameters', id: arg.deviceId },
        ],
      }),
    };
  },
});
